import type { HTMLAttributes, ReactNode } from "react";
import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Tone } from "@/lib/colors";

// Пилюля состояния. Тона = классы .pill-* из index.css + brand/info.

const TONES: Record<Tone, string> = {
  ok: "pill-ok",
  err: "pill-err",
  warn: "pill-warn",
  muted: "pill-muted",
  brand:
    "pill bg-brand-100 text-brand-700 dark:bg-brand-950/60 dark:text-brand-300",
  info: "pill bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300",
};

export function Pill({
  tone = "muted",
  size = "md",
  className,
  children,
  ...rest
}: HTMLAttributes<HTMLSpanElement> & {
  tone?: Tone;
  size?: "sm" | "md";
  children: ReactNode;
}) {
  return (
    <span
      {...rest}
      className={cn(
        TONES[tone],
        size === "sm" && "px-1.5 py-0 text-[11px] leading-4",
        className,
      )}
    >
      {children}
    </span>
  );
}

/**
 * Дельта в процентах: зелёная вверх, розовая вниз, серая при 0/нет данных.
 * `invert` — для метрик, где снижение = хорошо (позиция, cancel).
 * `pct` (по умолчанию true) — значение уже в процентах; false — доля (0.12 → 12%).
 */
export function DeltaPill({
  value,
  pct = true,
  invert,
  frac = 1,
  arrow,
  title,
  className,
}: {
  value: number | null | undefined;
  pct?: boolean;
  invert?: boolean;
  frac?: number;
  /** Стрелка вместо знака (стиль KpiCard). */
  arrow?: boolean;
  title?: string;
  className?: string;
}) {
  if (value == null || !Number.isFinite(value))
    return <span className="text-ink-400 text-xs">—</span>;
  const v = pct ? value : value * 100;
  if (v === 0)
    return (
      <Pill tone="muted" className={cn("text-xs", className)} title={title}>
        0%
      </Pill>
    );
  const up = v > 0;
  const good = invert ? !up : up;
  const text = `${v.toFixed(frac)}%`;
  return (
    <Pill
      tone={good ? "ok" : "err"}
      className={cn("text-xs", className)}
      title={title}
    >
      {arrow ? (
        <>
          {up ? (
            <ArrowUpRight className="h-3 w-3" />
          ) : (
            <ArrowDownRight className="h-3 w-3" />
          )}
          {Math.abs(v).toFixed(frac)}%
        </>
      ) : (
        (up ? "+" : "") + text
      )}
    </Pill>
  );
}

export default Pill;
